import React from 'react';
import { Send, FileText, FileClock, Percent, Star, AlertTriangle, ExternalLink, Mail, Square, ShieldCheck } from 'lucide-react';
import { Button } from '../../ui/button';

const EstimateActions = ({
  estimate,
  isProcessing,
  reviewRequestSent,
  isLowConfidence,
  sequenceStatus,
  isStartingSequence,
  onSendToCustomer,
  onDownloadPDF,
  onGenerateContract,
  onApplyDiscount,
  onSendForSigning,
  onSendWarranty,
  onRequestReview,
  onGetProfessionalMeasurement,
  onImportRoofr,
  onImportEagleview,
  onStartSequence,
  onStopSequence,
  handleProtectedAction
}) => {
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Actions</h2>
      
      {/* Low Confidence Warning Banner */}
      {isLowConfidence && !estimate?.measurement_verified && (
        <div className="mb-4 p-4 bg-amber-50 border-2 border-amber-400 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-bold text-amber-800">
                ⚠️ Low Measurement Confidence ({estimate?.measurement_confidence?.score || 0}%)
              </h4>
              <p className="text-amber-700 text-sm mt-1">
                Satellite data may be inaccurate for this property. For accurate quotes, import a professional measurement report.
              </p>
              <div className="mt-3 flex gap-2 flex-wrap">
                <Button 
                  size="sm" 
                  onClick={onGetProfessionalMeasurement}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                >
                  <ExternalLink className="w-4 h-4 mr-1" />
                  Get Professional Measurement
                </Button>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={onImportRoofr}
                  className="border-green-400 text-green-700"
                >
                  Import Roofr
                </Button>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={onImportEagleview}
                  className="border-blue-400 text-blue-700"
                >
                  Import Eagleview
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Button
          onClick={() => handleProtectedAction('send', onSendToCustomer)}
          disabled={isProcessing}
          className="bg-gradient-to-r from-blue-600 to-orange-500 hover:from-blue-700 hover:to-orange-600"
          data-testid="send-to-customer-btn"
        >
          <Send className="w-4 h-4 mr-2" />
          Send to Customer
        </Button>
        
        <Button
          onClick={onDownloadPDF}
          disabled={isProcessing}
          className="bg-blue-600 hover:bg-blue-700"
          data-testid="download-pdf-btn"
        >
          <FileText className="w-4 h-4 mr-2" />
          Download PDF
        </Button>
        
        <Button
          onClick={() => handleProtectedAction('contract', onGenerateContract)}
          disabled={isProcessing}
          className="bg-green-600 hover:bg-green-700"
          data-testid="generate-contract-btn"
        >
          <FileClock className="w-4 h-4 mr-2" />
          Generate Contract
        </Button>
        
        <Button
          onClick={onApplyDiscount}
          disabled={isProcessing}
          variant="outline"
          className="border-orange-300 text-orange-700 hover:bg-orange-50"
          data-testid="apply-discount-btn"
        >
          <Percent className="w-4 h-4 mr-2" />
          Apply Discount
        </Button>
        
        <Button
          onClick={() => handleProtectedAction('sign', onSendForSigning)}
          disabled={isProcessing}
          className="bg-purple-600 hover:bg-purple-700"
          data-testid="send-for-signing-btn"
        >
          <Send className="w-4 h-4 mr-2" />
          Send for Signing
        </Button>
        
        <Button
          onClick={onSendWarranty}
          disabled={isProcessing || !estimate?.customer_email}
          className="bg-blue-600 hover:bg-blue-700 text-white"
          data-testid="send-warranty-btn"
          title={!estimate?.customer_email ? 'Customer email required' : 'Send pre-signed 5-year workmanship warranty'}
        >
          <ShieldCheck className="w-4 h-4 mr-2" />
          Send Warranty
        </Button>
        
        <Button
          onClick={onRequestReview}
          disabled={isProcessing || reviewRequestSent}
          className={reviewRequestSent 
            ? "bg-green-100 text-green-700 border border-green-300" 
            : "bg-yellow-500 hover:bg-yellow-600 text-white"
          }
          data-testid="request-review-btn"
        >
          <Star className="w-4 h-4 mr-2" />
          {reviewRequestSent ? 'Review Requested' : 'Request Review'}
        </Button>

        {/* Follow-up Sequence Button */}
        {sequenceStatus?.active ? (
          <Button
            onClick={onStopSequence}
            disabled={isProcessing || isStartingSequence}
            variant="outline"
            className="border-red-300 text-red-700 hover:bg-red-50"
            data-testid="stop-sequence-btn"
          >
            <Square className="w-4 h-4 mr-2" />
            Stop Sequence ({sequenceStatus.emails_sent}/{sequenceStatus.emails_total} sent)
          </Button>
        ) : (
          <Button
            onClick={onStartSequence}
            disabled={isProcessing || isStartingSequence}
            className="bg-teal-600 hover:bg-teal-700 text-white"
            data-testid="start-sequence-btn"
          >
            <Mail className="w-4 h-4 mr-2" />
            {isStartingSequence ? 'Starting...' : 
              sequenceStatus?.status === 'completed' ? 'Restart Follow-up' : 'Start Follow-up Sequence'}
          </Button>
        )}
      </div>

      {/* Sequence Status Banner */}
      {sequenceStatus?.active && (
        <div className="mt-4 p-3 bg-teal-50 border border-teal-200 rounded-lg" data-testid="sequence-status-banner">
          <div className="flex items-center gap-2 text-teal-800 text-sm">
            <Mail className="w-4 h-4" />
            <span className="font-medium">Follow-up sequence active:</span>
            <span>{sequenceStatus.emails_sent} of {sequenceStatus.emails_total} emails sent, {sequenceStatus.emails_scheduled} pending</span>
          </div>
        </div>
      )}
      {sequenceStatus?.status === 'completed' && (
        <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-lg" data-testid="sequence-completed-banner">
          <div className="flex items-center gap-2 text-gray-600 text-sm">
            <Mail className="w-4 h-4" />
            <span>Follow-up sequence completed ({sequenceStatus.emails_sent}/{sequenceStatus.emails_total} sent)</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default EstimateActions;
